/**
 * 근태 정정 쿼리 키 팩토리.
 *
 * 근태 정정은 `entities/attendance` 안에 살지만 **루트가 다르다** — `attendanceKeys` 는
 * `'attendances'`, 이쪽은 `'corrections'` 다. 한 팩토리에 두 루트를 넣으면 제약이
 * `'attendances' | 'corrections'` 로 느슨해져 근태 키에 정정 루트를 적어도 통과하므로,
 * `defineQueryKeys` 를 각각 부른다. (같은 사유의 선례: `workLogKeys` / `teamWorkLogKeys`.)
 *
 * 루트가 갈려 있는 것은 의도다. 정정 생성·승인·반려는 **두 루트를 모두** 무효화해야 하는데
 * (정정 상태가 캘린더 날짜에 실려 나가므로 — `shared/types/attendance.ts` 의
 * `correction?: { status }`), 그건 mutation 이 근태 쪽 계약(`attendanceKeys`)에
 * `calendar` scope 를 함께 선언해서 처리한다.
 *
 * 하위 네임스페이스는 둘이다:
 * - `admin` — 결재자용 큐(`useCorrectionQueries`)
 * - `segment` — 구간 정정(`useSegmentCorrectionQueries`), 자체 scope 를 다시 가진다
 */
import { defineInvalidation, defineQueryKeys } from '@/shared/lib/queryKeys';

import type { ReviewedCorrectionsParams } from '../api/correctionApi';
import type { ReviewedSegmentCorrectionsParams } from '../api/segmentCorrectionApi';

const keys = defineQueryKeys('corrections', (all) => ({
  /**
   * ── 무효화 전용 중간 접두사 ────────────────────────────────────────────
   *
   * 조회에 쓰지 않는다. 뮤테이션이 **무엇을 무효화하는지 선언**할 때 쓴다
   * (아래 `defineInvalidation` 계약). 이름을 `attendanceKeys` 와 맞춰 `*Prefix` 로 둔다.
   */
  myPrefix: [...all, 'my'] as const,
  /** 결재자 큐 2종(`adminPending` · `adminReviewed`)을 한 번에 덮는다. */
  adminPrefix: [...all, 'admin'] as const,
  /** 구간 정정 3종(`segmentMy` · `segmentPending` · `segmentReviewed`)을 한 번에 덮는다. */
  segmentPrefix: [...all, 'segment'] as const,

  /** 본인 정정 이력. */
  my: () => [...all, 'my'] as const,
  myPending: (startDate: string, endDate: string) =>
    [...all, 'my', 'pending', { startDate, endDate }] as const,
  myPendingForAttendance: (attendanceId: string) =>
    [...all, 'my', 'pending', { attendanceId }] as const,

  /** 결재자 큐 — 대기. */
  adminPending: () => [...all, 'admin', 'pending'] as const,
  /** 결재자 큐 — 처리 완료. 조회 조건마다 별도 엔트리이고 캐시 모양은 `{ pages, pageParams }`(`useInfiniteQuery`)다. */
  adminReviewed: (params: ReviewedCorrectionsParams) =>
    [...all, 'admin', 'reviewed', params] as const,

  /** 구간 정정 — 본인. */
  segmentMy: () => [...all, 'segment', 'my'] as const,
  /** 구간 정정 — 대기. */
  segmentPending: () => [...all, 'segment', 'pending'] as const,
  /** 구간 정정 — 처리 완료. */
  segmentReviewed: (params: ReviewedSegmentCorrectionsParams) =>
    [...all, 'segment', 'reviewed', params] as const,
}));

/**
 * 무효화 계약.
 *
 * - `my` — 본인 정정 이력(`useMyCorrections`)
 * - `admin` — 결재자 큐 2종(대기 · 처리완료)
 * - `segment` — 구간 정정 3종(본인 · 대기 · 처리완료)
 *
 * 시각 정정과 구간 정정은 루트를 공유하되 **별도 엔드포인트·별도 테이블**이다
 * (`AttendanceCorrectionRequest` vs `AttendanceSegmentCorrectionRequest`). 한쪽 뮤테이션이
 * 다른 쪽 화면을 지울 이유가 없고, 그게 이 계약이 표현하려는 것이다.
 *
 * `correctionRoot` 는 **배럴로 내보내지 않는다**.
 */
export const {
  keys: correctionKeys,
  root: correctionRoot,
  invalidate: invalidateCorrectionScopes,
  invalidateAndWait: invalidateCorrectionScopesAndWait,
} = defineInvalidation(keys, {
  my: keys.myPrefix,
  admin: keys.adminPrefix,
  segment: keys.segmentPrefix,
});
